// Static city painting: all details are cosmetic and cached in one map texture.
import { CITY_ART, WORLD } from '../../common/src/data';
import { GameMap, T, TILE, tileAt, inTileRect, districtAt } from '../../common/src/map';
import { hash2 } from '../../common/src/util';
import { COLOR, PALETTE } from './theme';
import { mixHex } from './maplayout';
import { CityLight, paintBlocks, pavingAt, disc, px } from './blocks';
import './fronts-tokyo'; // registers the Okubo / Nishi-Shinjuku fronts, roofs, props and lamps

// Street signs (world.json / neon.signs) are no longer floating sprites: city-plan.ts turns them into façades.
export const inNeonBlock = (tx: number, ty: number) => inTileRect(tx, ty, CITY_ART.neon);
export const signColor = (color: string) => color === 'pink' ? CITY_ART.lights.pink : color === 'teal' ? CITY_ART.lights.teal : CITY_ART.lights.yellow;

/** One material per connected solid roof, rather than color patches across a building. Regions are numbered and
 *  flooded district by district, so adding or reshaping one district never recolors another's roofs. */
function roofRegions(m: GameMap) {
  const ids = new Int32Array(m.tiles.length).fill(-1);
  let region = 0;
  for (const d of WORLD.districts) for (let sy = d.y; sy < d.y + d.h; sy++) for (let sx = d.x; sx < d.x + d.w; sx++) {
    const start = sy * m.w + sx;
    if (m.tiles[start] !== T.BUILDING || ids[start] !== -1) continue;
    ids[start] = region;
    const stack = [start];
    while (stack.length) {
      const i = stack.pop()!, x = i % m.w, y = Math.floor(i / m.w);
      for (const [dx, dy] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) {
        const nx = x + dx, ny = y + dy;
        if (!inTileRect(nx, ny, d)) continue;
        const next = ny * m.w + nx;
        if (m.tiles[next] === T.BUILDING && ids[next] === -1) { ids[next] = region; stack.push(next); }
      }
    }
    region++;
  }
  return ids;
}

function roofUnit(g: CanvasRenderingContext2D, X: number, Y: number, r: number, shade: string) {
  px(g, X + 3, Y + 5, 12, 9, shade); // short cast shadow, contained in the solid tile
  if (r < 0.55) {
    px(g, X + 1, Y + 3, 12, 9, '#253143');
    px(g, X + 2, Y + 3, 10, 1, '#94a1b0'); px(g, X + 2, Y + 4, 10, 7, '#647488');
    for (const x of [X + 3, X + 8]) {
      px(g, x, Y + 5, 4, 4, '#263549'); px(g, x + 1, Y + 5, 1, 4, '#8e9caf'); px(g, x, Y + 6, 4, 1, '#8e9caf');
    }
  } else if (r < 0.8) {
    px(g, X + 2, Y + 4, 11, 8, '#223448'); px(g, X + 2, Y + 4, 11, 1, '#7798aa');
    px(g, X + 3, Y + 5, 9, 5, '#304f63'); px(g, X + 6, Y + 5, 1, 6, '#172b3a');
    px(g, X + 4, Y + 5, 1, 2, '#698497');
  } else {
    px(g, X + 3, Y + 3, 8, 9, '#263346'); px(g, X + 4, Y + 3, 6, 8, '#718195');
    px(g, X + 4, Y + 3, 6, 1, '#9ca7b5'); px(g, X + 4, Y + 7, 6, 1, '#4d5c70');
  }
}

function paintRoof(g: CanvasRenderingContext2D, m: GameMap, tx: number, ty: number, region: number) {
  const X = tx * TILE, Y = ty * TILE, r = hash2(tx, ty);
  const p = CITY_ART.roofs[region % CITY_ART.roofs.length];
  const open = (x: number, y: number) => tileAt(m, x, y) !== T.BUILDING;
  const n = open(tx, ty - 1), s = open(tx, ty + 1), w = open(tx - 1, ty), e = open(tx + 1, ty);
  px(g, X, Y, TILE, TILE, p.base);
  // Sparse panel seams and scuffs: larger flat roof shapes remain dominant.
  if (tx % 4 === 0) px(g, X, Y, 1, TILE, p.shade);
  if (ty % 5 === 0) px(g, X, Y, TILE, 1, p.shade);
  if (r > 0.93) px(g, X + 5, Y + 5, 3, 1, p.shade);
  if (!n && !s && !w && !e && tx % 3 === 0 && ty % 3 === 0 && r < CITY_ART.roofDetail) roofUnit(g, X + Math.floor(hash2(tx + 7, ty) * 2), Y + Math.floor(hash2(tx, ty + 5) * 3), hash2(ty, tx), p.shade);
  if (n) { px(g, X, Y, TILE, 2, p.shade); px(g, X, Y + 2, TILE, 1, p.light); }
  if (w) { px(g, X, Y, 2, TILE, p.shade); px(g, X + 2, Y, 1, TILE, p.light); }
  if (e) { px(g, X + 12, Y, 4, TILE, p.wall); px(g, X + 11, Y, 1, TILE, p.light); px(g, X + 15, Y, 1, TILE, '#111a28'); }
  if (s) {
    px(g, X, Y + 5, TILE, 1, p.light); px(g, X, Y + 6, TILE, 10, p.wall);
    const neon = inNeonBlock(tx, ty), shop = hash2(Math.floor(tx / 4), ty), part = tx % 4;
    const col = neon ? signColor(shop < 0.35 ? 'pink' : shop < 0.65 ? 'teal' : 'yellow') : shop < 0.55 ? CITY_ART.windows.warm : shop < 0.78 ? CITY_ART.windows.cool : CITY_ART.windows.dim;
    // Four-tile shop fronts have broad awnings and different window/door silhouettes.
    px(g, X + 2, Y + 9, 12, 5, col); px(g, X + 7, Y + 9, 1, 5, p.wall);
    if (part === 1) { px(g, X + 5, Y + 9, 6, 7, CITY_ART.windows.dark); px(g, X + 6, Y + 10, 4, 3, col); px(g, X + 9, Y + 14, 1, 1, col); }
    if (shop > 0.45) {
      px(g, X, Y + 6, TILE, 2, neon ? col : '#315b68');
      if (part === 0 || part === 3) px(g, X + (part === 0 ? 1 : 13), Y + 7, 2, 1, '#97b7bc');
    }
    if (part === 0) px(g, X, Y + 6, 1, 10, p.shade);
    px(g, X, Y + 15, TILE, 1, '#111a28');
  }
}

/** District paving (city-art.json `paving` zones): warm brick in Okubo, big pale granite slabs in Nishi-Shinjuku. */
function paving(g: CanvasRenderingContext2D, style: string, tx: number, ty: number) {
  const X = tx * TILE, Y = ty * TILE;
  if (style === 'gravel') { // shrine grounds: raked gravel with a stone path up the middle
    px(g, X, Y, TILE, TILE, '#4c4a46');
    for (let i = 0; i < 10; i++) px(g, X + Math.floor(hash2(tx * 9 + i, ty) * 15), Y + Math.floor(hash2(ty, tx * 9 + i) * 15), 1, 1, i % 2 ? '#62605a' : '#3a3834');
    return;
  }
  if (style === 'okubo') {
    px(g, X, Y, TILE, TILE, '#463a3c');
    for (let j = 0; j < TILE; j += 4) { px(g, X, Y + j, TILE, 1, '#352a2e'); for (let i = (j / 4 + tx) % 2 ? 4 : 0; i < TILE; i += 8) px(g, X + i, Y + j, 1, 4, '#352a2e'); }
    if (hash2(tx, ty) < 0.2) px(g, X + 5, Y + 9, 6, 2, '#5a4a4a');
  } else {
    px(g, X, Y, TILE, TILE, '#4a5462');
    if (tx % 2 === 0) px(g, X, Y, 1, TILE, '#5e6a7a');
    if (ty % 2 === 0) px(g, X, Y, TILE, 1, '#5e6a7a');
    if (hash2(tx, ty) < 0.1) px(g, X + 6, Y + 6, 2, 2, '#56606e');
  }
}
function sidewalk(g: CanvasRenderingContext2D, m: GameMap, tx: number, ty: number) {
  const X = tx * TILE, Y = ty * TILE, p = CITY_ART.surface, zone = pavingAt(tx, ty);
  if (zone) paving(g, zone, tx, ty);
  else { px(g, X, Y, TILE, TILE, p.sidewalk); px(g, X, Y, TILE, 1, p.sidewalkSeam); px(g, X, Y, 1, TILE, p.sidewalkSeam); }
  const edge = (x: number, y: number) => tileAt(m, x, y) === T.ROAD;
  if (edge(tx, ty + 1)) {
    px(g, X, Y + 14, TILE, 2, p.curb);
    if (tx % 5 === 0) { px(g, X + 3, Y + 12, 9, 3, '#142131'); for (let i = 4; i < 12; i += 2) px(g, X + i, Y + 12, 1, 2, '#697b8c'); }
  }
  if (edge(tx, ty - 1)) px(g, X, Y, TILE, 2, p.curb);
  if (edge(tx - 1, ty)) px(g, X, Y, 2, TILE, p.curb);
  if (edge(tx + 1, ty)) px(g, X + 14, Y, 2, TILE, p.curb);
  if (!zone && hash2(tx, ty) < 0.07) px(g, X + 7, Y + 6, 3, 1, '#435267');
}
/** Special low cover (city-art.json `lowArt`): shrine stone lanterns, granite planters, a metro stairway. */
function lowArt(g: CanvasRenderingContext2D, lights: CityLight[]) {
  for (const a of CITY_ART.lowArt) {
    const X = a.x * TILE, Y = a.y * TILE, W = a.w * TILE, H = a.h * TILE;
    for (let ty = a.y; ty < a.y + a.h; ty++) for (let tx = a.x; tx < a.x + a.w; tx++) paving(g, pavingAt(tx, ty) ?? 'nishi', tx, ty);
    if (a.kind === 'toro') { // stone lantern: base, post, lit firebox, cap
      px(g, X + 2, Y + H - 8, W - 4, 6, '#05070d'); px(g, X + 3, Y + H - 7, W - 6, 4, '#7a7e86');
      px(g, X + 6, Y + 8, 4, H - 16, '#8a8e96'); px(g, X + 3, Y + 6, 10, 9, '#05070d'); px(g, X + 4, Y + 7, 8, 7, '#9a9ea6'); px(g, X + 6, Y + 9, 4, 3, '#ffd080');
      px(g, X + 1, Y + 3, 14, 4, '#05070d'); px(g, X + 2, Y + 3, 12, 2, '#a8acb4');
      lights.push({ x: X + 5, y: Y + 8, w: 6, h: 5, color: '#ffb040', alpha: 0.4 });
    } else if (a.kind === 'planter') {
      px(g, X, Y + 1, W, H - 2, '#05070d'); px(g, X + 1, Y + 2, W - 2, H - 4, '#8a909c'); px(g, X + 2, Y + 3, W - 4, H - 6, '#2f6a3a');
      for (let i = X + 3; i < X + W - 3; i += 4) disc(g, i, Y + 6, 2, '#4a9a52');
    } else { // metro: stairs going down under a glass canopy, blue M roundel
      px(g, X, Y, W, H, '#05070d'); px(g, X + 2, Y + 2, W - 4, H - 4, '#2a3040');
      for (let j = Y + 5; j < Y + H - 3; j += 3) px(g, X + 4, j, W - 8, 1, '#5a6270');
      px(g, X + 1, Y + 1, W - 2, 2, '#9fc8dc'); px(g, X + 1, Y + 1, 2, H - 2, '#9fc8dc'); px(g, X + W - 3, Y + 1, 2, H - 2, '#9fc8dc');
      disc(g, X + W - 8, Y + 6, 5, '#05070d'); disc(g, X + W - 8, Y + 6, 4, '#2a6ad0'); px(g, X + W - 10, Y + 4, 1, 5, '#ffffff'); px(g, X + W - 6, Y + 4, 1, 5, '#ffffff'); px(g, X + W - 9, Y + 5, 2, 1, '#ffffff');
      lights.push({ x: X + W - 13, y: Y + 1, w: 10, h: 10, color: '#4a8aff', alpha: 0.45 });
    }
  }
}
const overlaps = (a: { x: number; y: number; w: number; h: number }, b: { x: number; y: number; w: number; h: number }) =>
  a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
/** Island platforms with a canopy on the tracks where the station stands (city-art.json `platforms`). */
function platforms(g: CanvasRenderingContext2D, m: GameMap, lights: CityLight[]) {
  for (const p of CITY_ART.platforms) {
    const r = WORLD.rails.find(rl => overlaps(rl, p))!, top = r.y * TILE, b = railBand(r.h * TILE);
    const y0 = top + b.tracks[0] + 8, y1 = top + b.tracks[1] - 8;
    for (let tx = p.x; tx < p.x + p.w; tx++) {
      if (tileAt(m, tx, r.y) !== T.RAIL) continue;
      const X = tx * TILE;
      px(g, X, y0, TILE, y1 - y0, '#8a8e98'); px(g, X, y0 + 1, TILE, 1, '#e8c43a'); px(g, X, y1 - 2, TILE, 1, '#e8c43a');
      px(g, X, y0 + 4, TILE, y1 - y0 - 8, '#5a6270'); px(g, X, y0 + 4, TILE, 1, '#8a94a6'); // canopy
      if (tx % 2 === 0) { px(g, X + 6, y0 + 7, 4, y1 - y0 - 14, '#9fc8dc'); lights.push({ x: X + 6, y: y0 + 7, w: 4, h: y1 - y0 - 14, color: '#dff4ff', alpha: 0.25 }); }
    }
  }
  // station concourses (plazas that cross a rail line): tactile guide line down the middle, glass railings over the tracks
  for (const c of WORLD.plazas.filter(pl => WORLD.rails.some(r => overlaps(r, pl)))) {
    const cx = (c.x + c.w / 2) * TILE;
    for (let y = c.y * TILE + 18; y < (c.y + c.h) * TILE - 18; y += 4) px(g, cx - 2, y, 4, 2, '#e8c43a');
    for (const r of WORLD.rails) {
      const y0 = Math.max(c.y, r.y) * TILE, y1 = Math.min(c.y + c.h, r.y + r.h) * TILE;
      if (y1 <= y0) continue;
      for (const x of [c.x * TILE, (c.x + c.w) * TILE - 3]) { px(g, x, y0, 3, y1 - y0, '#05070d'); px(g, x + 1, y0, 1, y1 - y0, '#9fc8dc'); }
      lights.push({ x: c.x * TILE, y: y0, w: c.w * TILE, h: y1 - y0, color: '#dff4ff', alpha: 0.12 });
    }
  }
}

// ------------------------------------------------------------------ station line (T.RAIL)
/** Track layout across a rail corridor, in px from its north edge: fences on both edges, two tracks, each with its
 *  two rails at these offsets from the track centre. */
function railBand(h: number) { return { fence: [1, h - 4], tracks: [Math.round(h * 0.3), Math.round(h * 0.7)], rails: [-6, 4] }; }
const railAt = (tx: number, ty: number) => WORLD.rails.find(r => inTileRect(tx, ty, r));
/** Clip a band-local rectangle (y measured from the corridor's top) to one tile row and paint it. */
function bandPx(g: CanvasRenderingContext2D, X: number, top: number, Y: number, x: number, y: number, w: number, h: number, col: string) {
  const y0 = Math.max(Y, top + y), y1 = Math.min(Y + TILE, top + y + h);
  if (y1 > y0) px(g, X + x, y0, w, y1 - y0, col);
}
function railTile(g: CanvasRenderingContext2D, tx: number, ty: number) {
  const r = railAt(tx, ty)!, X = tx * TILE, Y = ty * TILE, top = r.y * TILE, b = railBand(r.h * TILE);
  px(g, X, Y, TILE, TILE, '#24232a');
  for (let i = 0; i < 6; i++) { const v = hash2(tx * 7 + i, ty); px(g, X + Math.floor(v * 15), Y + Math.floor(hash2(ty, tx + i) * 15), 1, 1, v < 0.5 ? '#3a3840' : '#18171c'); }
  for (const c of b.tracks) {
    for (let i = 1; i < TILE; i += 5) bandPx(g, X, top, Y, i, c - 9, 3, 18, '#3d3128'); // sleepers
    for (const o of b.rails) { bandPx(g, X, top, Y, 0, c + o, TILE, 2, '#7d8796'); bandPx(g, X, top, Y, 0, c + o, TILE, 1, '#c3cbd6'); }
  }
  for (const f of b.fence) { // chain-link fence with posts
    bandPx(g, X, top, Y, 0, f, TILE, 2, '#3e4756'); bandPx(g, X, top, Y, 0, f, TILE, 1, '#7d8a9c');
    if (tx % 2 === 0) bandPx(g, X, top, Y, 1, f - 1, 2, 4, '#9aa6b6');
  }
}
/** Rails embedded in the road at level crossings, warning stripes and signal posts with red lamps. */
function levelCrossings(g: CanvasRenderingContext2D, m: GameMap, lights: CityLight[]) {
  for (const r of WORLD.rails) {
    const top = r.y * TILE, h = r.h * TILE, b = railBand(h);
    for (let tx = r.x; tx < r.x + r.w; tx++) {
      const t = tileAt(m, tx, r.y);
      if (t !== T.ROAD && t !== T.SIDEWALK) continue;
      const X = tx * TILE;
      for (const c of b.tracks) for (const o of b.rails) { px(g, X, top + c + o - 1, TILE, 4, '#0b0d12'); px(g, X, top + c + o, TILE, 2, '#8d97a6'); }
      if (t === T.ROAD) for (const y of [top + 2, top + h - 6]) for (let i = 0; i < TILE; i += 4) px(g, X + i, y, 2, 4, (tx + i / 4) % 2 ? '#e8c43a' : '#14161c');
      // signal posts on the sidewalk corners beside each roadway
      const roadW = tileAt(m, tx - 1, r.y) === T.ROAD, roadE = tileAt(m, tx + 1, r.y) === T.ROAD;
      if (t === T.SIDEWALK && (roadW || roadE)) for (const y of [top - 2, top + h - 8]) {
        const x = X + (roadW ? 3 : 7);
        px(g, x, y, 6, 10, '#05070d'); px(g, x + 1, y + 1, 4, 8, '#e8e8f0'); px(g, x + 1, y + 3, 4, 2, '#e8433e');
        lights.push({ x: x + 1, y: y + 3, w: 4, h: 2, color: '#ff4040', alpha: 0.3 });
      }
    }
  }
}

/** The sanctuary countryside in the 1x art (?hd=0, tests): a plain readable version of the 2x painting, with the
 *  winding road as asphalt, the walkable forest as dark green floor dotted with tree crowns, and pond/pool water. */
const C1 = { road: CITY_ART.surface.road, line: mixHex(PALETTE.amber, PALETTE.midnight, 0.4), forest: mixHex(PALETTE.midnight, mixHex(PALETTE.slate, PALETTE.cyan, 0.25), 0.4),
  crown: mixHex(PALETTE.midnight, mixHex(PALETTE.cyan, PALETTE.amber, 0.3), 0.6), sakura: mixHex(PALETTE.pink, PALETTE.offWhite, 0.35), water: mixHex(PALETTE.cyan, PALETTE.midnight, 0.7) };
function country1x(g: CanvasRenderingContext2D, t: T, tx: number, ty: number, X: number, Y: number, r: number) {
  if (ty < 46) return false;
  if (t === T.ALLEY && !WORLD.alleys.some(a => inTileRect(tx, ty, a))) { px(g, X, Y, TILE, TILE, C1.road); if (r < 0.1) px(g, X + 6, Y + 7, 4, 1, C1.line); return true; }
  if (t === T.LOW && WORLD.lows.some(l => (l.w > 1 || l.h > 1) && inTileRect(tx, ty, l))) { px(g, X, Y, TILE, TILE, C1.water); if (r < 0.3) px(g, X + 3, Y + 6, 6, 1, PALETTE.cyan); return true; }
  if (t !== T.BOLLARD) return false;
  px(g, X, Y, TILE, TILE, C1.forest);
  if (r < 0.35) disc(g, X + 8, Y + 8, 6, (WORLD.sanctuary?.features ?? []).some(f => f.kind === 'sakura' && inTileRect(tx, ty, f)) && r < 0.2 ? C1.sakura : C1.crown);
  return true;
}

export function paintCity(g: CanvasRenderingContext2D, m: GameMap, lights: CityLight[] = []) {
  const roofs = roofRegions(m), at = (x: number, y: number) => tileAt(m, x, y), p = CITY_ART.surface;
  for (let ty = 0; ty < m.h; ty++) for (let tx = 0; tx < m.w; tx++) {
    const t = at(tx, ty), X = tx * TILE, Y = ty * TILE, r = hash2(tx, ty);
    if (tx >= 256 && country1x(g, t, tx, ty, X, Y, r)) continue;
    switch (t) {
      case T.ROAD:
        px(g, X, Y, TILE, TILE, p.road);
        if (r < 0.15) px(g, X + 3, Y + 5, 7, 2, p.roadSpeckle);
        if (r > 0.98) { px(g, X + 5, Y + 5, 6, 5, '#1f2d3e'); px(g, X + 6, Y + 6, 4, 3, p.road); }
        break;
      case T.SIDEWALK: sidewalk(g, m, tx, ty); break;
      case T.ALLEY: case T.BOLLARD:
        px(g, X, Y, TILE, TILE, p.alley);
        if (r < 0.2) px(g, X + 3, Y + 5, 8, 1, '#33404f');
        if (t === T.BOLLARD && districtAt(tx, ty).id !== 'sanctuary') for (let i = 0; i < 2; i++) { // (the sanctuary's bollard ground is forest floor)
          px(g, X + 3 + i * 8, Y + 5, 3, 6, '#ffe03a'); px(g, X + 3 + i * 8, Y + 5, 3, 1, '#fff6a8'); px(g, X + 3 + i * 8, Y + 10, 3, 1, '#7a6a10');
        }
        break;
      case T.PLAZA: {
        const zone = pavingAt(tx, ty);
        if (zone) { paving(g, zone, tx, ty); break; }
        px(g, X, Y, TILE, TILE, p.plaza); px(g, X, Y, TILE, 1, p.sidewalkSeam); px(g, X, Y, 1, TILE, p.sidewalkSeam);
        if ((tx + ty) % 3 === 0) px(g, X + 7, Y + 7, 2, 2, '#465267');
        break;
      }
      case T.LOW:
        px(g, X, Y, TILE, TILE, p.sidewalk);
        px(g, X + 1, Y + 2, 14, 12, '#6a3a1a'); px(g, X + 1, Y + 2, 14, 2, '#9a5a2a'); px(g, X + 1, Y + 12, 14, 2, '#3a1a0a');
        for (let i = 0; i < 3; i++) px(g, X + 3 + i * 4, Y + 6, 2, 3, '#d8ff3a');
        break;
      case T.THIN:
        px(g, X, Y, TILE, TILE, p.sidewalk);
        px(g, X + 1, Y + 1, 14, 14, 'rgba(45,226,200,0.35)'); px(g, X + 1, Y + 1, 14, 1, '#2de2c8'); px(g, X + 1, Y + 14, 14, 1, '#16786c');
        px(g, X + 3, Y + 3, 1, 6, '#bff8ee'); px(g, X + 5, Y + 3, 1, 3, '#bff8ee');
        for (let i = 0; i < TILE; i += 4) px(g, X + i, Y + 7, 2, 1, '#2de2c8');
        break;
      case T.STALL: {
        const awning = tx % 2 ? '#ab4269' : '#326b7d';
        px(g, X, Y, TILE, TILE, '#172232'); px(g, X + 1, Y + 1, 14, 11, awning);
        for (let i = 2; i < 15; i += 4) px(g, X + i, Y + 1, 2, 11, '#c4b69b');
        px(g, X + 1, Y + 12, 14, 3, '#82624b'); px(g, X + 2, Y + 12, 12, 1, '#d5ad75');
        if (r < 0.5) { px(g, X + 3, Y + 13, 3, 2, '#e98b3f'); px(g, X + 9, Y + 13, 3, 2, '#ffe08c'); }
        break;
      }
      case T.RAIL: railTile(g, tx, ty); break;
      default: paintRoof(g, m, tx, ty, roofs[ty * m.w + tx]);
    }
  }
  lowArt(g, lights);
  platforms(g, m, lights);
  paintBlocks(g, m, lights);
  // Existing traffic geometry and crosswalk positions, painted after surface detail.
  for (const ry of m.hRoads) for (let tx = 0; tx < m.w; tx++) {
    const inter = m.vRoads.some(vx => tx >= vx && tx < vx + m.roadW);
    if (at(tx, ry) !== T.ROAD || inter) continue;
    const y = (ry + m.roadW / 2) * TILE;
    if (tx % 2 === 0) px(g, tx * TILE + 2, y - 1, 10, 2, '#d0ad46');
    if (m.vRoads.some(vx => tx === vx - 1 || tx === vx + m.roadW)) for (let i = 0; i < m.roadW * TILE; i += 6) px(g, tx * TILE + 3, ry * TILE + i + 1, 10, 3, '#d3dce5');
  }
  for (const rx of m.vRoads) for (let ty = 0; ty < m.h; ty++) {
    const inter = m.hRoads.some(hy => ty >= hy && ty < hy + m.roadW);
    if (at(rx, ty) !== T.ROAD || inter) continue;
    const x = (rx + m.roadW / 2) * TILE;
    if (ty % 2 === 0) px(g, x - 1, ty * TILE + 2, 2, 10, '#d0ad46');
    if (m.hRoads.some(hy => ty === hy - 1 || ty === hy + m.roadW)) for (let i = 0; i < m.roadW * TILE; i += 6) px(g, rx * TILE + i + 1, ty * TILE + 3, 3, 10, '#d3dce5');
  }
  levelCrossings(g, m, lights);
  if (lights.length > CITY_ART.effects.maxLights) { // keep the brightest within the glow budget, after every painter
    lights.sort((p, q) => q.alpha - p.alpha); lights.length = CITY_ART.effects.maxLights;
  }
  // Shop/kiosk reach decals keep their existing colors and positions.
  for (const point of WORLD.pois) {
    g.strokeStyle = poiColor(point.kind); g.lineWidth = 1; g.setLineDash([3, 2]);
    g.strokeRect(point.x * TILE - 14.5, point.y * TILE - 14.5, 29, 29); g.setLineDash([]);
  }
  // Recovery pad by the clinic: stand on it to heal.
  for (const z of WORLD.healZones) {
    const x = z.x * TILE, y = z.y * TILE, w = z.w * TILE, h = z.h * TILE;
    if (z.circle) { // a round zone (the onsen)
      g.fillStyle = 'rgba(45,226,200,0.2)'; g.strokeStyle = '#2de2c8'; g.lineWidth = 1;
      g.beginPath(); g.arc(z.circle.cx * TILE, z.circle.cy * TILE, z.circle.r * TILE - 1, 0, Math.PI * 2); g.fill(); g.stroke();
      continue;
    }
    g.fillStyle = 'rgba(45,226,200,0.2)'; g.fillRect(x, y, w, h);
    g.strokeStyle = '#2de2c8'; g.lineWidth = 1; g.strokeRect(x + 1.5, y + 1.5, w - 3, h - 3);
    px(g, x + w / 2 - 2, y + h / 2 - 7, 4, 14, '#e4fdff'); px(g, x + w / 2 - 7, y + h / 2 - 2, 14, 4, '#e4fdff');
  }
  // Karaoke den zone: a pink outline and a stairwell mouth (the 2x art draws the full sign)
  for (const s of WORLD.safehouses) if (s.kind === 'den') {
    const x = s.x * TILE, y = s.y * TILE, w = s.w * TILE, h = s.h * TILE;
    g.fillStyle = PALETTE.pink + '29'; g.fillRect(x, y, w, h);
    g.strokeStyle = PALETTE.pink; g.lineWidth = 1; g.strokeRect(x + 1.5, y + 1.5, w - 3, h - 3);
    px(g, x + 4, y + 4, 18, h - 8, PALETTE.midnight); for (let i = 0; i < 4; i++) px(g, x + 5, y + 6 + i * 5, 16, 2, PALETTE.slate);
  }
}

/** colour of a shop/kiosk's reach square (the same on the map and the minimap); hd-scene.ts paints them at 2x */
export const poiColor = (_kind: string) => COLOR.interactive;
